import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { type CustomCalibreEntry } from '../../services/firebase';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import {
  brandScopeManufacturerIds,
  calibreTiedToBrandViaManufacturers,
} from '../../services/calibreClassification';
import { useCalibreSearch } from '../../hooks/useCalibreSearch';

/**
 * Calibre link picker — brand-scope toggle + search + candidate list, mirroring
 * the CalibreActionToolbar "Add as AKA" picker shape (PR #266). Where that flow
 * attaches an alias, this one SELECTS a calibre and returns its full entry so
 * the ref editor can set `WatchReference.calibreId` (Phase 3a). Brand-scope
 * matching reuses the same lenient logic: direct brandId/brandName, plus the
 * co-developer / in-house case via `calibreTiedToBrandViaManufacturers`.
 *
 * Candidates come from Algolia (`useCalibreSearch`), which replaced the
 * `getCustomCalibres()` full collection read — ~1,822 documents on every open of
 * this modal — with a ranked shortlist hydrated back to real documents. The
 * search box now tolerates typos and accents rather than demanding the exact
 * spelling a substring filter needed.
 *
 * Brand scope is applied TWICE on purpose: server-side so an empty search box
 * still shows the brand's calibres rather than a global page, and then locally
 * on the hydrated documents, because the index's `allManufacturerIds` is
 * ancestor-inflated and would otherwise widen "Current brand only" beyond what
 * `calibreTiedToBrandViaManufacturers` considers a tie.
 */
export function CalibrePickerModal({
  isOpen,
  brandName,
  brandId,
  currentCalibreId,
  currentCalibreString,
  onSelect,
  onClose,
}: {
  isOpen: boolean;
  brandName: string;
  brandId?: string;
  /** Currently-linked calibre id, badged in the list so the pick is obvious. */
  currentCalibreId?: string;
  /**
   * The ref's current free-text calibre string (legacy value). Shown in the
   * header so the user knows what they're matching, and used to pre-seed the
   * search so likely matches surface immediately.
   */
  currentCalibreString?: string;
  onSelect: (entry: CustomCalibreEntry) => void;
  onClose: () => void;
}) {
  const hasBrandContext = brandName.trim().length > 0 || !!brandId;
  const [scope, setScope] = useState<'brand' | 'all'>(
    hasBrandContext ? 'brand' : 'all',
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setScope(hasBrandContext ? 'brand' : 'all');
    // Pre-seed the search with the ref's legacy calibre string so likely
    // matches are already filtered in; the user can clear/edit it.
    setSearch(currentCalibreString?.trim() || '');
  }, [isOpen, hasBrandContext, currentCalibreString]);

  // The manufacturer catalogue: what turns a brand into the set of manufacturer
  // ids that count as "this brand's makers", both for the Algolia filter and for
  // the local tie check below. Loaded once per open, not per keystroke.
  //
  // On Algolia since watchlock #867 indexed `brandIdsManualInclude` — the
  // hand-curated allowlist both `brandScopeManufacturerIds` and
  // `calibreTiedToBrandViaManufacturers` read. Until then this had to be the
  // full collection read: on an entry without that field the allowlist would
  // have read as absent for every manufacturer, silently collapsing brand scope
  // to the in-house case where a manufacturer's doc id happens to equal the
  // brand slug — a shorter pick list that looks entirely plausible.
  // Through the shared hook, which keeps the once-per-open fetch but also
  // listens for manufacturers created or deleted elsewhere in the app — brand
  // scope is computed from this list, so a stale one silently narrows the picks.
  const { manufacturers } = useManufacturerCatalogue({ enabled: isOpen });

  const manufacturerById = useMemo(
    () => new Map(manufacturers.map((m) => [m.id, m])),
    [manufacturers],
  );

  // Server-side scope. Omitted entirely for "All brands" so the query stays a
  // plain name search.
  const filters = useMemo(() => {
    if (scope !== 'brand' || !brandId) return {};
    return {
      brandId,
      manufacturerIds: brandScopeManufacturerIds(brandId, manufacturers),
    };
  }, [scope, brandId, manufacturers]);

  const { results, loading, degraded } = useCalibreSearch(search, {
    enabled: isOpen,
    filters,
    hitsPerPage: 100,
    // Brand context with no `brandId` (a legacy free-text `brandName` only) has
    // no index filter that can express it, so the scope below has to run over
    // the whole collection or it would under-report.
    exhaustive: scope === 'brand' && !brandId,
  });

  const candidates = useMemo(() => {
    if (!isOpen) return [];
    const brandLower = brandName.toLowerCase();
    // Re-applied on the hydrated documents: `allManufacturerIds` is inflated
    // with heritage ancestors, so the Algolia filter is a superset of what
    // counts as a brand tie. In degraded (full-scan) mode it is the ONLY scope
    // filter, since none of the server-side ones ran.
    const scoped =
      scope === 'brand'
        ? results.filter((c) => {
            if (brandId && c.brandId === brandId) return true;
            if (!!c.brandName && c.brandName.toLowerCase() === brandLower)
              return true;
            return (
              !!brandId &&
              calibreTiedToBrandViaManufacturers(c, brandId, manufacturerById)
            );
          })
        : results;
    // Ranked order is Algolia's, and it is the point of the search — only the
    // unfiltered (empty-box) list is alphabetised, which is what a browse list
    // wants.
    const ordered = search.trim()
      ? scoped
      : [...scoped].sort((a, b) => a.name.localeCompare(b.name));
    return ordered.slice(0, 100);
  }, [isOpen, results, manufacturerById, scope, search, brandName, brandId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Link a calibre' size='lg'>
      <div className='space-y-3'>
        <div className='text-sm text-gray-700'>
          Pick the calibre to link this reference to. Linking sets{' '}
          <span className='font-mono'>calibreId</span> and auto-populates the
          effective spec cache on save.
        </div>
        {currentCalibreString?.trim() && (
          <div className='text-xs text-gray-400'>
            Currently:{' '}
            <span className='font-mono text-gray-500'>
              &ldquo;{currentCalibreString.trim()}&rdquo;
            </span>
          </div>
        )}
        <div className='flex items-center gap-2 flex-wrap'>
          {hasBrandContext && (
            <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-xs'>
              <button
                type='button'
                onClick={() => setScope('brand')}
                className={`px-3 py-1.5 ${
                  scope === 'brand'
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Current brand only
              </button>
              <button
                type='button'
                onClick={() => setScope('all')}
                className={`px-3 py-1.5 border-l border-gray-300 ${
                  scope === 'all'
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                All brands
              </button>
            </div>
          )}
          <input
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search calibres…'
            className='flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-brand-500 focus:border-brand-500'
          />
        </div>
        {degraded && (
          <div className='text-xs text-amber-700'>
            Search is unavailable — showing an unranked local match over every
            calibre. Spelling has to be exact.
          </div>
        )}
        <div className='max-h-[50vh] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100'>
          {loading && candidates.length === 0 ? (
            <div className='p-3 text-sm text-gray-500 italic'>Loading…</div>
          ) : candidates.length === 0 ? (
            <div className='p-3 text-sm text-gray-500 italic'>
              No calibres match.
            </div>
          ) : (
            candidates.map((c) => (
              <button
                key={c.id}
                type='button'
                onClick={() => onSelect(c)}
                className='w-full text-left p-2 hover:bg-brand-50 text-sm flex items-baseline gap-2'
              >
                <span className='font-mono font-medium'>{c.name}</span>
                {c.id === currentCalibreId && (
                  <span className='rounded bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase'>
                    linked
                  </span>
                )}
                {(c.brandId || c.brandName) && (
                  <span className='text-gray-500 text-xs'>
                    ({c.brandId || c.brandName})
                  </span>
                )}
                {c.data?.movementType && (
                  <span className='text-gray-400 text-xs'>
                    — {c.data.movementType}
                  </span>
                )}
                {c.data?.tiers && c.data.tiers.length > 0 && (
                  <span className='text-gray-400 text-xs'>
                    · {c.data.tiers.length} tier
                    {c.data.tiers.length === 1 ? '' : 's'}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
        <div className='flex justify-end pt-2 border-t border-gray-100'>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
